import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { postSignIn } from '../services/signin/crudFunctions';
import { selectAuthLoading, selectAuthError } from '../services/signin/selectors';
import { RootState } from '../../../utils/type';
import { isAuthenticated, isAdmin } from '../../../utils/auth';

export const SignIn: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const loading = useSelector((state: RootState) => selectAuthLoading(state));
  const apiError = useSelector((state: RootState) => selectAuthError(state));

  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [identifierErr, setIdentifierErr] = useState('');
  const [passwordErr, setPasswordErr] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  // Redirect if already logged in
  useEffect(() => {
    if (isAuthenticated()) {
      navigate(isAdmin() ? '/admin/vendor-payments' : '/vendor/charger-status');
    }
  }, [navigate]);

  // Redirect after successful login
  useEffect(() => {
    if (!loading && !apiError && isAuthenticated()) {
      navigate(isAdmin() ? '/admin/vendor-payments' : '/vendor/charger-status');
    }
  }, [loading, apiError, navigate]);

  const validate = (): boolean => {
    let valid = true;
    if (!identifier.trim()) {
      setIdentifierErr('Email or mobile number is required.');
      valid = false;
    } else {
      setIdentifierErr('');
    }
    if (!password) {
      setPasswordErr('Password is required.');
      valid = false;
    } else {
      setPasswordErr('');
    }
    return valid;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    dispatch<any>(postSignIn({ identifier: identifier.trim(), password }));
  };

  return (
    <div
      style={{
        background: 'linear-gradient(135deg, #2A3F54 0%, #34495e 100%)',
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <div className="container-fluid">
        <div className="row justify-content-center">
          <div className="col-xl-4 col-lg-5 col-md-6 col-sm-8 col-10">
            <div className="card shadow-lg">
              <div className="card-body p-5">
                {/* Brand */}
                <div className="text-center mb-4">
                  <div className="d-flex align-items-center justify-content-center mb-3">
                    <span
                      style={{
                        fontSize: 32,
                        color: '#1abb9c',
                        marginRight: 10,
                      }}
                    >
                      ⚡
                    </span>
                    <h3 className="mb-0 fw-bold text-dark">EV Care</h3>
                  </div>
                  <p className="text-muted">Admin Panel — Please sign in</p>
                </div>

                {/* API Error */}
                {apiError && (
                  <div className="alert alert-danger py-2" role="alert">
                    <i className="fas fa-exclamation-triangle me-2" />
                    {apiError}
                  </div>
                )}

                <form onSubmit={handleSubmit} noValidate>
                  {/* Identifier */}
                  <div className="mb-3">
                    <label htmlFor="identifier" className="form-label text-muted">
                      Email or Mobile Number
                    </label>
                    <div className="input-group">
                      <span className="input-group-text bg-light">
                        <i className="fas fa-user text-muted" />
                      </span>
                      <input
                        type="text"
                        className={`form-control${identifierErr ? ' is-invalid' : ''}`}
                        id="identifier"
                        placeholder="Enter email or mobile number"
                        value={identifier}
                        onChange={(e) => {
                          setIdentifier(e.target.value);
                          setIdentifierErr('');
                        }}
                        autoComplete="username"
                      />
                      {identifierErr && (
                        <div className="invalid-feedback">{identifierErr}</div>
                      )}
                    </div>
                  </div>

                  {/* Password */}
                  <div className="mb-4">
                    <label htmlFor="password" className="form-label text-muted">
                      Password
                    </label>
                    <div className="input-group">
                      <span className="input-group-text bg-light">
                        <i className="fas fa-lock text-muted" />
                      </span>
                      <input
                        type={showPassword ? 'text' : 'password'}
                        className={`form-control${passwordErr ? ' is-invalid' : ''}`}
                        id="password"
                        placeholder="Enter your password"
                        value={password}
                        onChange={(e) => {
                          setPassword(e.target.value);
                          setPasswordErr('');
                        }}
                        autoComplete="current-password"
                      />
                      <button
                        type="button"
                        className="btn btn-outline-secondary"
                        onClick={() => setShowPassword((v) => !v)}
                        tabIndex={-1}
                        style={{ height: 37, color: 'black', border: '1px solid black' }}
                      >
                        <i className={`fas ${showPassword ? 'fa-eye-slash' : 'fa-eye'}`} />
                      </button>
                      {passwordErr && (
                        <div className="invalid-feedback">{passwordErr}</div>
                      )}
                    </div>
                  </div>

                  <div className="d-grid">
                    <button
                      type="submit"
                      className="btn btn-primary btn-lg"
                      disabled={loading}
                      style={{ background: '#1abb9c', borderColor: '#1abb9c' }}
                    >
                      {loading ? (
                        <>
                          <span
                            className="spinner-border spinner-border-sm me-2"
                            role="status"
                            aria-hidden="true"
                          />
                          Signing in...
                        </>
                      ) : (
                        <>
                          <i className="fas fa-sign-in-alt me-2" />
                          Sign In
                        </>
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </div>

            <div className="text-center mt-4">
              <p className="text-light opacity-75 mb-0">
                &copy; {new Date().getFullYear()} EV Care. All Rights Reserved.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
